import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Search,
    Settings,
    LogOut,
    HardDrive,
    FolderKanban,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, humanBytes } from "../lib/api";
import NotificationsBell from "./NotificationsBell";

const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["admin", "customer"] },
    { to: "/customers", label: "Klanten", icon: Users, testid: "nav-customers", roles: ["admin"] },
    { to: "/search", label: "Zoeken", icon: Search, testid: "nav-search", roles: ["admin", "customer"] },
    { to: "/settings", label: "Instellingen", icon: Settings, testid: "nav-settings", roles: ["admin", "customer"] },
];

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [branding, setBranding] = useState(null);

    // White-label: customer users see their own logo + name in the sidebar
    useEffect(() => {
        if (user?.role !== "customer") return;
        api.get("/branding")
            .then((r) => setBranding(r.data))
            .catch(() => {});
    }, [user?.role]);

    const brandName = branding?.name || "Social Factory";
    const brandLogo = branding?.logo_url || null;

    const used = user?.storage_used_bytes || 0;
    const quota = user?.storage_quota_bytes || 1;
    const pct = Math.min(100, (used / quota) * 100);
    const role = user?.role || "customer";
    const visibleNav = navItems.filter((it) => it.roles.includes(role));

    // Close drawer when route changes
    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const SidebarBody = (
        <>
            <div className="px-5 py-5 border-b border-zinc-200 flex items-center justify-between">
                <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
                    {brandLogo ? (
                        <div className="w-8 h-8 rounded-sm border border-zinc-200 bg-white overflow-hidden flex items-center justify-center">
                            <img src={brandLogo} alt={brandName} className="w-full h-full object-contain" />
                        </div>
                    ) : (
                        <div className="w-8 h-8 bg-[#09090b] rounded-sm flex items-center justify-center">
                            <FolderKanban className="w-4 h-4 text-white" />
                        </div>
                    )}
                    <div>
                        <div className="font-display text-base leading-none tracking-tighter">
                            {brandName}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                            AI Portal
                        </div>
                    </div>
                </Link>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="lg:hidden p-2 -mr-2 text-zinc-500 hover:text-zinc-900 rounded-md"
                    aria-label="Sluit menu"
                    data-testid="sidebar-close"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {visibleNav.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={it.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                                isActive
                                    ? "bg-[#09090b] text-white"
                                    : "text-zinc-700 hover:bg-zinc-100"
                            }`
                        }
                    >
                        <it.icon className="w-4 h-4" />
                        {it.label}
                    </NavLink>
                ))}
            </nav>

            <div className="m-3 p-4 border border-zinc-200 rounded-md bg-zinc-50" data-testid="storage-summary">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">
                    <HardDrive className="w-3 h-3" /> Opslag
                </div>
                <div className="text-sm font-mono mb-2">
                    {humanBytes(used)} / {humanBytes(quota)}
                </div>
                <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all ${
                            pct > 90 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-[#0044FF]"
                        }`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            <div className="border-t border-zinc-200 p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#0044FF] text-white flex items-center justify-center font-semibold text-sm shrink-0">
                    {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{user?.name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                        {user?.role === "admin" ? "Beheerder" : "Klant"}
                    </div>
                </div>
                <button
                    onClick={() => {
                        logout();
                        navigate("/login");
                    }}
                    className="p-2 text-zinc-500 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
                    data-testid="logout-button"
                    title="Uitloggen"
                    aria-label="Uitloggen"
                >
                    <LogOut className="w-4 h-4" />
                </button>
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-[#f4f4f5]">
            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="p-2 -ml-2 text-zinc-700 rounded-md hover:bg-zinc-100"
                    aria-label="Open menu"
                    data-testid="mobile-menu-button"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <Link to="/dashboard" className="flex items-center gap-2">
                    {brandLogo ? (
                        <div className="w-7 h-7 rounded-sm border border-zinc-200 bg-white overflow-hidden flex items-center justify-center">
                            <img src={brandLogo} alt={brandName} className="w-full h-full object-contain" />
                        </div>
                    ) : (
                        <div className="w-7 h-7 bg-[#09090b] rounded-sm flex items-center justify-center">
                            <FolderKanban className="w-3.5 h-3.5 text-white" />
                        </div>
                    )}
                    <div className="font-display text-sm leading-none tracking-tighter">
                        {brandName}
                    </div>
                </Link>
                <NotificationsBell />
            </header>

            <div className="flex">
                {/* Desktop sidebar */}
                <aside
                    className="hidden lg:flex w-64 border-r border-zinc-200 bg-white h-screen sticky top-0 flex-col"
                    data-testid="sidebar"
                >
                    {SidebarBody}
                </aside>

                {/* Mobile drawer */}
                {open && (
                    <>
                        <button
                            type="button"
                            aria-label="Sluit menu"
                            onClick={() => setOpen(false)}
                            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] animate-fade-in"
                        />
                        <aside
                            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white border-r border-zinc-200 flex flex-col shadow-xl animate-fade-in"
                            data-testid="mobile-sidebar"
                        >
                            {SidebarBody}
                        </aside>
                    </>
                )}

                <main className="flex-1 min-w-0">
                    {/* Desktop notifications bell */}
                    <div className="hidden lg:flex sticky top-0 z-10 bg-[#f4f4f5] px-10 pt-4 -mb-2 justify-end">
                        <NotificationsBell />
                    </div>
                    {children}
                </main>
            </div>
        </div>
    );
}
